import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import { RateOwnerType } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useContext, useEffect } from 'react';

import { QuickFilterButtonGroup } from '~components/QuickFilterButtonGroup/QuickFilterButtonGroup';
import SettingsPageHeader from '~pages/Settings/components/SettingsPageHeader';
import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import AutomaticAddonsListContainer from './components/AutomaticAddonsDataGrid';
import RecurringAddOnsExplainerBanner from './components/RecurringAddOnsExplainerBanner';
import ScheduleOwnerListContainer from './components/ScheduleOwnerList';
import { OwnerContext, OwnerProvider } from './context/OwnerContext';

const AutomaticSettlementAddOnsContent = observer(() => {
  const theme = useTheme();
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);
  const { selectedOwnerType, setSelectedOwnerType, selectedOwnerId, setSelectedOwner } =
    useContext(OwnerContext);

  useEffect(() => {
    setSelectedOwner('');
    setSelectedOwnerType(RateOwnerType.VENDOR);
  }, [companyId]);

  const handleOwnerChange = (ownerType: RateOwnerType) => {
    setSelectedOwner('');
    setSelectedOwnerType(ownerType);
  };

  const buttons = [
    {
      title: t('administration.rates.vendor'),
      value: RateOwnerType.VENDOR,
      action: () => handleOwnerChange(RateOwnerType.VENDOR),
    },
    {
      title: t('administration.rates.customer'),
      value: RateOwnerType.CUSTOMER,
      action: () => handleOwnerChange(RateOwnerType.CUSTOMER),
    },
    {
      title: t('administration.rates.internal_drivers'),
      value: RateOwnerType.DRIVER,
      action: () => handleOwnerChange(RateOwnerType.DRIVER),
    },
  ];

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 153px - var(--tread-agent-dock-height, 0px))',
      }}
    >
      <SettingsPageHeader
        title={t('settings.nav_items.recurring_settlement_add_ons')}
        description={t('settings.page_descriptions.recurring_settlement_add_ons')}
      />

      <Box sx={{ maxHeight: '50%', overflowY: 'auto' }}>
        <RecurringAddOnsExplainerBanner />
      </Box>

      <Box
        sx={{
          border: `solid 1px ${theme.palette.divider}`,
          display: 'flex',
          flex: 1,
          mb: -4,
          minHeight: 0,
          mt: 2,
        }}
      >
        {/* Left Column */}
        <Box
          sx={{
            borderRight: `solid 1px ${theme.palette.divider}`,
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
            minWidth: '300px',
            maxWidth: '350px',
          }}
        >
          <Box
            sx={{
              m: 1.5,
              alignSelf: 'center',
            }}
          >
            <QuickFilterButtonGroup
              buttons={buttons}
              color={theme.palette.action.selected}
              currentActiveFilter={selectedOwnerType}
            />
          </Box>

          <Divider />

          <ScheduleOwnerListContainer
            companyId={companyId}
            ownerType={selectedOwnerType}
          />
        </Box>

        {/* Right Column */}
        <Box
          sx={{
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
            minWidth: 0,
            overflow: 'hidden',
          }}
        >
          <AutomaticAddonsListContainer
            ownerId={selectedOwnerId}
            ownerType={selectedOwnerType}
          />
        </Box>
      </Box>
    </Box>
  );
});

const AutomaticSettlementAddOns = () => {
  return (
    <OwnerProvider>
      <AutomaticSettlementAddOnsContent />
    </OwnerProvider>
  );
};

export default AutomaticSettlementAddOns;
